import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import API from "@/lib/api";
import {
  FlaskConical, RefreshCw, Award, Mail, Download, CheckCircle2, XCircle, Clock,
  Pill, Shield, AlertTriangle, Eye,
} from "lucide-react";
import SandboxEventsDrawer from "@/components/prescriber/SandboxEventsDrawer";

/**
 * Prescriber Training Sandbox tab.
 *
 * Shows 12 seeded practice orders. Clicking a row opens the SAME approve/
 * deny modal the live queue uses — the backend recognises `is_sandbox:true`
 * and stubs every outbound effect (LifeFile push, patient email, SMS,
 * charge). When the prescriber closes all 12, a Certificate of Completion
 * is minted and shown at the top of the tab with View / Print / Email
 * PDF options.
 *
 * Admins land here read-only; they can see the orders and events but
 * approve/deny is prescriber-only.
 */
export default function SandboxTab({ onOpenOrderAction }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [eventsOrderId, setEventsOrderId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/sandbox/orders");
      setOrders(data.orders || []);
      setCertificate(data.certificate || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sandbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetSandbox = async () => {
    if (!confirm("Reset your sandbox? This wipes your current practice run and reseeds all 12 fresh.")) return;
    setResetting(true);
    try {
      await API.delete("/sandbox/orders");
      await load();
      toast.success("Sandbox reset — 12 fresh practice orders are ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const downloadCert = async () => {
    if (!certificate?.cert_id) return;
    try {
      const res = await API.get(`/sandbox/certificate/${certificate.cert_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GLPFlo_training_cert_${certificate.cert_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const printCert = async () => {
    if (!certificate?.cert_id) return;
    try {
      const res = await API.get(`/sandbox/certificate/${certificate.cert_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) {
        w.onload = () => { try { w.print(); } catch { /* noop */ } };
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    }
  };

  const emailCert = async () => {
    if (!certificate?.cert_id) return;
    const to = prompt("Email the certificate to (leave blank to send to your personal email):", "");
    setEmailing(true);
    try {
      const body = to && to.trim() ? { to: to.trim() } : {};
      const { data } = await API.post(`/sandbox/certificate/${certificate.cert_id}/email`, body);
      toast.success(`Certificate emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const closed = orders.filter((o) => ["approved", "denied", "cancelled"].includes((o.status || "").toLowerCase())).length;
  const pct = orders.length > 0 ? Math.round((closed / orders.length) * 100) : 0;

  const statusChip = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>;
    if (s === "denied") return <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"><XCircle className="w-3 h-3 mr-1" /> Denied</Badge>;
    return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  };

  const tagIcon = (tag) => {
    if (tag === "allergy") return <AlertTriangle className="w-3 h-3" />;
    if (tag === "polypharmacy") return <Pill className="w-3 h-3" />;
    if (tag === "safety") return <Shield className="w-3 h-3" />;
    return null;
  };

  return (
    <div className="space-y-5" data-testid="sandbox-tab">
      <div className="rounded-xl border border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30 p-4 flex items-start gap-3">
        <FlaskConical className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-orange-900 dark:text-orange-200">PRACTICE MODE — nothing is sent to the pharmacy</h2>
          <p className="text-xs text-orange-800 dark:text-orange-300 mt-1">
            Approve or deny all 12 practice orders using the full flow. LifeFile pushes, patient emails, SMS, and charges are stubbed on sandbox orders — the exact "would-have-sent" event is written to your sandbox audit log. When you finish all 12, your Certificate of Completion auto-mints below.
          </p>
        </div>
        <Button onClick={resetSandbox} disabled={resetting} variant="outline" size="sm" data-testid="sandbox-reset-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${resetting ? "animate-spin" : ""}`} />
          Reset my sandbox
        </Button>
      </div>

      {certificate ? (
        <div className="rounded-xl border-2 border-purple-400 dark:border-purple-700 bg-gradient-to-br from-purple-50 to-fuchsia-50 dark:from-purple-950/40 dark:to-fuchsia-950/40 p-5" data-testid="sandbox-certificate-card">
          <div className="flex items-start gap-4 flex-wrap">
            <Award className="w-10 h-10 text-purple-600 flex-shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <p className="text-[11px] font-bold uppercase tracking-widest text-purple-700 dark:text-purple-300">Certificate earned</p>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">GLPFlo Prescriber Training Sandbox</h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                Cert <code className="bg-white dark:bg-slate-800 px-1 rounded">{certificate.cert_id}</code> ·
                Approved {certificate.approved_count} · Denied {certificate.denied_count} ·
                Issued {new Date(certificate.issued_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={downloadCert} data-testid="sandbox-cert-download-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> View / Download PDF
              </Button>
              <Button size="sm" variant="outline" onClick={printCert} data-testid="sandbox-cert-print-btn">
                Print
              </Button>
              <Button size="sm" variant="outline" onClick={emailCert} disabled={emailing} data-testid="sandbox-cert-email-btn">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email PDF
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-800 p-4" data-testid="sandbox-progress">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Training progress — {closed} / {orders.length} orders closed
            </p>
            <span className="text-xs text-slate-500">{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                 style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-800 overflow-hidden" data-testid="sandbox-orders-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left">
            <tr>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200">Scenario</th>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200">Patient</th>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200">Medication</th>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200">Complexity</th>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200">Status</th>
              <th className="px-4 py-2 font-semibold text-slate-700 dark:text-slate-200 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">Loading your sandbox…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">No sandbox orders yet. Refresh to seed.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                  <div className="font-medium">{o.sandbox_scenario_label}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{o.id}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                  <div>{o.patient_name}</div>
                  <div className="text-[11px] text-slate-400">{o.patient_state}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                  <div>{o.medication_name}</div>
                  <div className="text-[11px] text-slate-400">{o.requested_strength} · {o.payment_type.replace("_", " ")}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(o.complexity_tags || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        {tagIcon(t)} {t.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">{statusChip(o.status)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEventsOrderId(o.id)}
                      data-testid={`sandbox-events-btn-${o.sandbox_scenario_slug || o.id}`}
                      title="See what would have fired"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Events
                    </Button>
                    <Button
                      size="sm"
                      variant={["approved", "denied"].includes((o.status || "").toLowerCase()) ? "outline" : "default"}
                      onClick={() => onOpenOrderAction?.(o)}
                      data-testid={`sandbox-open-order-${o.sandbox_scenario_slug || o.id}`}
                    >
                      {["approved", "denied"].includes((o.status || "").toLowerCase()) ? "View" : "Review"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400 text-center">
        All sandbox orders carry <code>is_sandbox:true</code>. No real patient receives any communication from this tab. Tap <b>Events</b> on any order to see the full lifecycle of stubbed side-effects.
      </p>

      <SandboxEventsDrawer
        orderId={eventsOrderId}
        open={!!eventsOrderId}
        onOpenChange={(v) => { if (!v) setEventsOrderId(null); }}
      />
    </div>
  );
}
